import { useEffect } from 'react';
import type { IntlShape } from '@atlassian/jira-intl';
import { useFieldValue } from '@atlassian/jira-issue-field-base';
import { TimeEstimateKey } from '@atlassian/jira-providers-issue';
import { timeTrackingFormatter } from '@atlassian/jira-time-tracking-formatter';
import { useDiffCheck } from '../../../../common/hooks/use-diff-check';
import type { CardId, Card } from '../../../../model/types';
import { useBacklogSelector, useBacklogActionCreator } from '../../../../state';
import { cardEstimateSet } from '../../../../state/actions/estimate';
import { getTimeTrackingOptions } from '../../../../state/selectors/app';
import { isOriginalEstimateTypeSelector } from '../../../../state/selectors/estimation';

type Props = {
	card: Card;
	intl: IntlShape;
};

const FieldSyncOriginalEstimate = ({ card: { id, key, estimate }, intl }: Props) => {
	const timeTrackingOptions = useBacklogSelector(getTimeTrackingOptions);
	const isOriginalEstimateType = useBacklogSelector(isOriginalEstimateTypeSelector);

	const setOriginalEstimateInBacklog = useBacklogActionCreator(
		(cardId: CardId, value: number | null) => {
			const originalEstimate =
				value === null
					? null
					: {
							value,
							valueAsText: timeTrackingFormatter(value, timeTrackingOptions, intl),
						};

			return cardEstimateSet(cardId, { originalEstimate });
		},
	);
	const estimateFromBacklog = estimate?.originalEstimate;
	const originalEstimateFromBacklog = estimateFromBacklog?.value ?? null;

	// This hook updates the state used by bento in the field value service. It's necessary to provide the initial value
	// and to update the bento store when the original estimate changes from elsewhere in the backlog
	const [
		originalEstimateFromExtractedFields,
		{ setFieldValue: setOriginalEstimateInExtractedFields },
	] = useFieldValue({
		issueKey: key,
		fieldKey: TimeEstimateKey,
	});

	const originalEstimateFromBacklogChanged = useDiffCheck([originalEstimateFromBacklog]);
	const originalEstimateFromExtractedFieldsChanged = useDiffCheck([
		originalEstimateFromExtractedFields,
	]);

	const storesNeedToSync = originalEstimateFromBacklog !== originalEstimateFromExtractedFields;

	useEffect(() => {
		const originalEstimateFromBacklogIsDefined = estimateFromBacklog !== undefined;

		const shouldSync =
			originalEstimateFromBacklogIsDefined &&
			originalEstimateFromBacklogChanged &&
			storesNeedToSync &&
			isOriginalEstimateType;

		// ORIGINAL ESTIMATE SYNC: BACKLOG -> EXTRACTED FIELDS
		// Update sweet state (extracted field) when the original estimate value changes elsewhere or the card is initially rendered
		if (shouldSync) {
			setOriginalEstimateInExtractedFields(key, TimeEstimateKey, originalEstimateFromBacklog);
		}
	}, [
		key,
		estimateFromBacklog,
		originalEstimateFromBacklog,
		originalEstimateFromBacklogChanged,
		storesNeedToSync,
		setOriginalEstimateInExtractedFields,
		isOriginalEstimateType,
	]);

	useEffect(() => {
		const originalEstimateFromExtractedFieldsIsDefined =
			originalEstimateFromExtractedFields !== undefined;

		const shouldSync =
			!originalEstimateFromBacklogChanged &&
			originalEstimateFromExtractedFieldsIsDefined &&
			originalEstimateFromExtractedFieldsChanged &&
			storesNeedToSync &&
			isOriginalEstimateType;

		// ORIGINAL ESTIMATE SYNC: EXTRACTED FIELDS -> BACKLOG
		// Update the backlog store if the value of the original estimate is changed within extracted fields
		if (shouldSync) {
			setOriginalEstimateInBacklog(id, originalEstimateFromExtractedFields);
		}
	}, [
		id,
		originalEstimateFromBacklogChanged,
		originalEstimateFromExtractedFields,
		originalEstimateFromExtractedFieldsChanged,
		storesNeedToSync,
		setOriginalEstimateInBacklog,
		isOriginalEstimateType,
	]);

	return null;
};

export default FieldSyncOriginalEstimate;
